import { ReactNode, useState } from 'react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import { useReduxState } from 'hooks';

interface Props {
  align?: PopoverPrimitive.PopoverContentProps['align'];
  children: (setIsOpen: (open: boolean) => void) => ReactNode;
  content: ReactNode;
  forceMount?: true;
  side?: PopoverPrimitive.PopoverContentProps['side'];
}

const Popover = ({
  align = 'center',
  children,
  content,
  forceMount,
  side = 'top'
}: Props) => {
  const { theme } = useReduxState();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  return (
    <PopoverPrimitive.Root
      defaultOpen={false}
      onOpenChange={handleOpenChange}
      open={isOpen}
    >
      <PopoverPrimitive.Anchor>{children(setIsOpen)}</PopoverPrimitive.Anchor>
      <PopoverPrimitive.Content
        align={align}
        className={`vuo-popover-content ${
          forceMount ? 'vuo-popover-content-forcemount' : ''
        }`}
        forceMount={forceMount}
        side={side}
      >
        {content}
        <PopoverPrimitive.Arrow className="vuo-popover-arrow" />
      </PopoverPrimitive.Content>

      <style jsx global>{`
        .vuo-popover-content {
          animation: vuoPopoverScaleIn 0.2s cubic-bezier(0.35, 0, 0.25, 1);
          background-color: #fff;
          border-radius: ${theme.dropdownBorderRadius};
          box-shadow: ${theme.dropdownShadow};
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 600;
          padding: 10px 15px;
          position: relative;
          top: -5px;
          transform-origin: center bottom 0;
        }
        .vuo-popover-content-forcemount[data-state='closed'] {
          display: none;
        }
        .vuo-popover-content-forcemount[data-state='open'] {
          display: block;
        }
        @keyframes vuoPopoverScaleIn {
          from {
            height: 0;
            opacity: 0;
            overflow: hidden;
            transform: scale(0.8);
            visibility: hidden;
          }
          to {
            height: auto;
            opacity: 1;
            overflow: visible;
            transform: none;
            visibility: visible;
          }
        }
        .vuo-popover-arrow {
          fill: #fff;
        }
      `}</style>
    </PopoverPrimitive.Root>
  );
};

export default Popover;
